import { useEffect, useMemo, useState } from 'react'

import {
  buildUsersAccessRows,
  getNextUsersAccessInviteExpiration,
} from '../utils/usersAccess'
import * as Sentry from '@sentry/nextjs'

import {
  useManagedAccountsQuery,
  usePendingInvitesQuery,
  useTechniciansQuery,
} from '@/graphql/generated/hooks'

export const useUsersAccessRows = () => {
  const [currentTime, setCurrentTime] = useState(() => Date.now())
  const {
    data: usersData,
    loading: isUsersLoading,
    error: usersError,
    refetch: refetchUsers,
  } = useManagedAccountsQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  })
  const {
    data: techniciansData,
    loading: isTechniciansLoading,
    error: techniciansError,
    refetch: refetchTechnicians,
  } = useTechniciansQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  })
  const {
    data: pendingInvitesData,
    loading: isPendingInvitesLoading,
    error: pendingInvitesError,
    refetch: refetchPendingInvites,
  } = usePendingInvitesQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  })

  useEffect(() => {
    const queryErrors = [
      ['managed_accounts', usersError],
      ['technicians', techniciansError],
      ['pending_invites', pendingInvitesError],
    ] as const

    queryErrors.forEach(([source, error]) => {
      if (!error) return
      Sentry.captureException(error, {
        tags: {
          action: 'load',
          feature: 'managed_accounts',
          source,
          surface: 'settings_users_access',
        },
      })
    })
  }, [usersError, techniciansError, pendingInvitesError])

  useEffect(() => {
    const nextExpiration = getNextUsersAccessInviteExpiration(
      pendingInvitesData,
      currentTime
    )
    if (nextExpiration === null) return

    const timeoutId = window.setTimeout(
      () => setCurrentTime(Date.now()),
      Math.max(nextExpiration - Date.now() + 1, 0)
    )
    return () => window.clearTimeout(timeoutId)
  }, [pendingInvitesData, currentTime])

  const rows = useMemo(
    () =>
      buildUsersAccessRows(
        usersData,
        techniciansData,
        pendingInvitesData,
        currentTime
      ),
    [usersData, techniciansData, pendingInvitesData, currentTime]
  )

  const retry = () => {
    // Refetch failures update each query's error state and are captured by the
    // effect above, so this catch intentionally avoids reporting them twice.
    void Promise.all([
      refetchUsers(),
      refetchTechnicians(),
      refetchPendingInvites(),
    ]).catch(() => undefined)
  }

  return {
    rows,
    isLoading:
      isUsersLoading || isTechniciansLoading || isPendingInvitesLoading,
    isError: Boolean(usersError || techniciansError || pendingInvitesError),
    retry,
  }
}
